import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const BOOKINGS_API = `${API_BASE}/bookings`;
const INVOICES_API = `${API_BASE}/invoices`;
const STRIPE_API = `${API_BASE}/stripe`;
const MONEYWORKS_API = `${API_BASE}/moneyworks`;

export default function BookingDetail() {
  const { id, ref: refParam } = useParams();
  const bookingId = id || refParam;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showOwnersRiskCapture, setShowOwnersRiskCapture] = useState(false);
  const [ownersRiskName, setOwnersRiskName] = useState("");
  const autoCalcAttempted = useRef(false);

  useEffect(() => {
    loadBooking();
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      setLoading(true);

      // Try direct ID fetch first
      const directRes = await fetch(`${BOOKINGS_API}/${bookingId}`, { cache: "no-store" });
      if (directRes.ok) {
        const data = await directRes.json();
        const b = data?.data || data;
        if (b && b._id) {
          console.log("Booking loaded (direct):", b.bookingRef, b.livestockItems);
          setBooking(b);
          maybeAutoCalculateCharges(b);
          return;
        }
      }

      // Fallback — fetch all and find by id/ref
      const allRes = await fetch(BOOKINGS_API, { cache: "no-store" });
      const allData = await allRes.json();
      const list = Array.isArray(allData) ? allData : (allData?.data || allData?.bookings || []);
      const found = list.find(item => {
        const shortId = item._id?.slice(-6).toUpperCase();
        return item._id === bookingId || item.bookingRef === bookingId || shortId === bookingId?.toUpperCase();
      });
      console.log("Booking loaded (fallback):", found?.bookingRef, found?.livestockItems);
      setBooking(found || null);
      maybeAutoCalculateCharges(found);
    } catch (error) {
      console.error("Failed to load booking:", error);
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  // Runs once per page load. If the booking already has cargo (freight or
  // livestock items) but the charges block is still all zero — meaning it
  // hasn't been priced yet — trigger the same calculation the "Calculate
  // Charges" button uses, so staff reviewing a Pending booking see real
  // numbers straight away instead of $0.00 with no explanation.
  const maybeAutoCalculateCharges = (b) => {
    if (!b?._id) return;
    if (autoCalcAttempted.current) return;

    const hasCargo = (b.freightItems?.length || 0) > 0 || (b.livestockItems?.length || 0) > 0;
    const alreadyPriced = Number(b.charges?.total || 0) > 0;
    const isFinal = ["cancelled", "completed"].includes(String(b.status || "").toLowerCase());

    if (hasCargo && !alreadyPriced && !isFinal) {
      autoCalcAttempted.current = true;
      fetch(`${INVOICES_API}/calculate/${b._id}`, { method: "POST" })
        .then(res => (res.ok ? res.json() : null))
        .then(() => {
          // Re-fetch quietly so the calculated charges show without a second
          // full loading spinner or overwriting anything the user has open.
          fetch(`${BOOKINGS_API}/${b._id}`, { cache: "no-store" })
            .then(res => (res.ok ? res.json() : null))
            .then(data => {
              const refreshed = data?.data || data;
              if (refreshed?._id) setBooking(refreshed);
            });
        })
        .catch(err => console.error("Auto-calculate charges failed:", err));
    }
  };

  const lineItems = useMemo(() => {
    const livestock =
      booking?.livestockItems?.map((item, index) => ({
        id: `livestock-${index}`,
        category: "Livestock",
        description: item.livestockType || "Livestock",
        quantity: item.headCount || 0,
        unitType: "head",
        weight:
          item.totalWeightKg ||
          Number(item.headCount || 0) * Number(item.averageWeightKg || 0),
        cubic: 0,
        lengthM: 0,
        linearM: 0,
      })) || [];

    const freight =
      booking?.freightItems?.map((item, index) => ({
        id: `freight-${index}`,
        category: "Freight",
        description: item.description || item.freightType || "Freight",
        quantity: item.quantity || 0,
        unitType: item.freightType || "-",
        weight: item.weightKg || 0,
        cubic: item.cubeM3 || 0,
        lengthM: Number(item.lengthM || 0),
        linearM: Number(item.lengthM || 0) * Number(item.quantity || 1),
      })) || [];

    return [...livestock, ...freight];
  }, [booking]);

  const totalWeight = lineItems.reduce((sum, item) => sum + Number(item.weight || 0), 0);
  const totalCube = lineItems.reduce((sum, item) => sum + Number(item.cubic || 0), 0);
  const totalQuantity = lineItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalLinearM = lineItems.reduce((sum, item) => sum + Number(item.linearM || 0), 0);

  const bookingRef =
    booking?.bookingRef ||
    booking?.ref ||
    booking?.reference ||
    booking?._id?.slice(-6).toUpperCase();

  const currentStatus = String(booking?.status || "pending").toLowerCase();
  const hasMoneyworksInvoice = Boolean(booking?.invoice?.moneyworksSequenceNumber);
  const defaultInvoiceEmail =
    booking?.onlineRequest?.sender?.email ||
    booking?.onlineRequest?.receiver?.email ||
    booking?.customer?.email ||
    "";

  const updateStatus = async (newStatus, ownersRiskOverride) => {
    if (!booking?._id) return;

    try {
      setSavingStatus(true);

      const response = await fetch(`${BOOKINGS_API}/${booking._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...booking,
          status: newStatus,
          ...(ownersRiskOverride ? { ownersRiskStatement: ownersRiskOverride } : {}),
          workflowSnapshot: {
            ...(booking.workflowSnapshot || {}),
            lastStatus: booking.status || "pending",
            currentStatus: newStatus,
            updatedAt: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) throw new Error(await response.text());
      await loadBooking();
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  // Confirming requires the Owner's Risk Statement to already be executed
  // (e.g. captured at online submission) — if it isn't, show the capture
  // panel instead of letting the request hit the server and fail there.
  const handleConfirmClick = () => {
    if (booking?.ownersRiskStatement?.executed) {
      updateStatus("confirmed");
    } else {
      setShowOwnersRiskCapture(true);
    }
  };

  const handleConfirmWithOwnersRisk = () => {
    if (!ownersRiskName.trim()) return;
    updateStatus("confirmed", {
      executed: true,
      signedName: ownersRiskName.trim(),
      executedAt: new Date().toISOString(),
      executedVia: "staff_manual",
    });
    setShowOwnersRiskCapture(false);
  };

  const handleCalculateCharges = async () => {
    if (!booking?._id) return;

    try {
      setSavingStatus(true);

      const response = await fetch(`${INVOICES_API}/calculate/${booking._id}`, {
        method: "POST",
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to calculate charges");

      await loadBooking();
      alert("Charges calculated");
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const [customAmount, setCustomAmount] = useState("");
  const [actionsOpen, setActionsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onDown = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [actionsOpen]);

  const handleCreatePaymentLink = async () => {
    if (!booking?._id) return;
    try {
      setSavingStatus(true);
      const custom = Number(customAmount);
      const response = await fetch(`${STRIPE_API}/payment-link/${booking._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(custom > 0 ? { customAmount: custom } : {}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to create payment link");
      await loadBooking();
      try { await navigator.clipboard.writeText(data.url); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); } catch (e) { /* clipboard may be blocked */ }
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSendStripeInvoice = async () => {
    if (!booking?._id) return;
    const email = (invoiceEmail || defaultInvoiceEmail || "").trim();
    if (!email) { alert("Enter an email address to send the invoice to."); return; }
    try {
      setSavingStatus(true);
      const custom = Number(customAmount);
      const response = await fetch(`${STRIPE_API}/send-invoice/${booking._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...(custom > 0 ? { customAmount: custom } : {}) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to send invoice");
      await loadBooking();
      alert(`Invoice ${data.number || ""} for $${Number(data.amount).toFixed(2)} (incl. GST) sent to ${data.sentTo}.${data.bankDetailsIncluded ? "" : "\n\nNote: no bank details are set up yet (INVOICE_BANK_DETAILS), so only card payment is shown."}`);
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSendToMoneyworks = async () => {
    if (!booking?._id) return;

    try {
      setSavingStatus(true);

      const response = await fetch(`${MONEYWORKS_API}/create-invoice/${booking._id}`, {
        method: "POST",
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to send to Moneyworks");

      await loadBooking();
      alert(`Moneyworks invoice created: ${data.invoiceNumber || "Draft"}`);
    } catch (error) {
      alert(error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return <PageShell>Loading booking...</PageShell>;
  }

  if (!booking) {
    return (
      <PageShell>
        <Link to="/dashboard/bookings" className="btn-secondary">
          ← Back
        </Link>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          Booking not found.
        </div>
      </PageShell>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full p-6">
        <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Link to="/dashboard/bookings" className="btn-secondary">
                ← Back
              </Link>
              <StatusBadge status={booking.status} />
              <SourceBadge source={booking.source} />
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Booking {bookingRef}
            </h1>

            <p className="mt-2 text-sm text-slate-600">
              {booking.customerName || "Unknown Customer"} ·{" "}
              {booking.bookingType || booking.type || "Unassigned"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Primary action — the single next step for this status */}
            {currentStatus === "pending" && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={handleConfirmClick}
                className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                Confirm Booking
              </button>
            )}
            {currentStatus === "confirmed" && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={handleSendToMoneyworks}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {hasMoneyworksInvoice ? "Resend to Moneyworks" : "Send to Moneyworks"}
              </button>
            )}
            {currentStatus === "invoiced" && (
              <button
                type="button"
                disabled={savingStatus}
                onClick={() => updateStatus("completed")}
                className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
              >
                Complete Booking
              </button>
            )}

            <Link
              to={`/dashboard/bookings/${booking._id}/edit`}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Edit
            </Link>

            {/* Everything else lives in one menu */}
            <div className="relative" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={() => setActionsOpen((o) => !o)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Actions ▾
              </button>

              {actionsOpen && (
                <div className="absolute right-0 z-20 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                  <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Documents
                  </div>
                  <Link
                    to={`/dashboard/bookings/${booking._id}/print`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setActionsOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Print Confirmation
                  </Link>
                  <Link
                    to={`/dashboard/bookings/${booking._id}/consignment-note`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setActionsOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Consignment Note
                  </Link>

                  {["pending", "confirmed", "invoiced"].includes(currentStatus) && (
                    <>
                      <div className="mt-1 border-t border-slate-100 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        Charges
                      </div>
                      <button
                        type="button"
                        disabled={savingStatus}
                        onClick={() => { setActionsOpen(false); handleCalculateCharges(); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Calculate Charges
                      </button>
                    </>
                  )}

                  {currentStatus !== "cancelled" && currentStatus !== "completed" && (
                    <>
                      <div className="my-1 border-t border-slate-100" />
                      <button
                        type="button"
                        disabled={savingStatus}
                        onClick={() => { setActionsOpen(false); updateStatus("cancelled"); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel Booking
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {showOwnersRiskCapture && (
          <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 space-y-3">
            <p className="text-sm font-semibold text-slate-900">
              Owner's Risk Statement required to confirm
            </p>
            <blockquote className="text-sm italic text-slate-700 border-l-2 border-slate-300 pl-3">
              "These goods are to be carried at owner's risk. This means that the
              carrier will not pay compensation if the goods are lost or damaged
              unless he intentionally loses or damages them."
            </blockquote>
            <p className="text-xs text-slate-500">
              This booking hasn't recorded an executed Owner's Risk Statement yet. Confirm the customer has read and agreed before proceeding.
            </p>
            <input
              type="text"
              value={ownersRiskName}
              onChange={(e) => setOwnersRiskName(e.target.value)}
              placeholder="Customer's full name"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-950"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={savingStatus || !ownersRiskName.trim()}
                onClick={handleConfirmWithOwnersRisk}
                className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                Record &amp; Confirm Booking
              </button>
              <button
                type="button"
                onClick={() => setShowOwnersRiskCapture(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
          <div className="space-y-6">
            <SectionCard title="Workflow">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                <WorkflowStep label="Pending" active={currentStatus === "pending"} done={["confirmed", "invoiced", "completed"].includes(currentStatus)} />
                <WorkflowStep label="Confirmed" active={currentStatus === "confirmed"} done={["invoiced", "completed"].includes(currentStatus)} />
                <WorkflowStep label="Invoiced" active={currentStatus === "invoiced"} done={currentStatus === "completed"} />
                <WorkflowStep label="Completed" active={currentStatus === "completed"} done={currentStatus === "completed"} />
                <WorkflowStep label="Cancelled" active={currentStatus === "cancelled"} danger />
              </div>
            </SectionCard>

            <SectionCard title="Movement Details">
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-3">
                <Info label="Customer" value={booking.customerName || "-"} />
                <Info label="Booking Type" value={booking.bookingType || booking.type || "-"} />
                <Info label="Status" value={toTitle(booking.status || "-")} />
                <Info label="Total Quantity" value={totalQuantity} />
                <Info label="Total Weight" value={`${totalWeight.toLocaleString()} kg`} />
                <Info label="Total Cube" value={`${totalCube.toLocaleString()} m³`} />
                {totalLinearM > 0 && (
                  <Info label="Total Linear Metres" value={`${totalLinearM.toLocaleString()} m`} />
                )}
              </div>
            </SectionCard>

            <SectionCard title="Pricing" flush>
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 p-6 md:grid-cols-2 xl:grid-cols-4">
                <Info label="Freight Charge" value={money(booking.charges?.freightCharge)} />
                <Info label="Livestock Charge" value={money(booking.charges?.livestockCharge)} />
                <Info label="MPI Charges" value={money(booking.charges?.mpiCharges)} />
                <Info label="Council Levy" value={money(booking.charges?.councilLevy)} />
                <Info label="FAF" value={money(booking.charges?.fuelAdjustment)} />
                <Info label="GST" value={money(booking.charges?.gst)} />
                <Info label="Subtotal" value={money(booking.charges?.subtotal)} />
              </div>
              <div className="flex items-center justify-between rounded-b-3xl bg-slate-950 px-6 py-3.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total</span>
                <span className="text-lg font-bold text-white">{money(booking.charges?.total)}</span>
              </div>
            </SectionCard>

            <SectionCard title="Cargo Details" flush>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr className="text-left">
                      <th className="px-5 py-4">Category</th>
                      <th className="px-5 py-4">Description</th>
                      <th className="px-5 py-4">Qty</th>
                      <th className="px-5 py-4">Unit</th>
                      <th className="px-5 py-4">Weight</th>
                      <th className="px-5 py-4">Cube</th>
                      <th className="px-5 py-4">Length / item</th>
                      <th className="px-5 py-4">Linear metres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.length > 0 ? (
                      lineItems.map((item) => (
                        <tr key={item.id} className="border-t border-slate-200">
                          <td className="px-5 py-4">{item.category}</td>
                          <td className="px-5 py-4 font-medium">{item.description}</td>
                          <td className="px-5 py-4">{item.quantity}</td>
                          <td className="px-5 py-4">{item.unitType}</td>
                          <td className="px-5 py-4">{item.weight} kg</td>
                          <td className="px-5 py-4">{item.cubic} m³</td>
                          <td className="px-5 py-4">{item.lengthM > 0 ? `${item.lengthM} m` : "-"}</td>
                          <td className="px-5 py-4 font-medium">{item.linearM > 0 ? `${item.linearM.toLocaleString()} m` : "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                          No cargo details recorded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Notes">
              <p className="whitespace-pre-wrap text-sm text-slate-700">
                {booking.notes || "No notes recorded."}
              </p>
            </SectionCard>

            {/* Livestock declarations */}
            {(booking.bookingType === "livestock" || booking.bookingType === "mixed") && booking.livestockItems?.length > 0 && (
              <SectionCard title="Livestock Declarations & Welfare">
                {booking.livestockItems.map((item, i) => (
                  <div key={i} className={`${i > 0 ? "border-t border-slate-100 mt-4 pt-4" : ""}`}>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 mb-4">
                      <Info label="Species" value={toTitle(item.livestockType || "—")} />
                      <Info label="Class" value={toTitle(item.animalClass || "—")} />
                      <Info label="Head Count" value={item.headCount} />
                      <Info label="Avg Weight" value={item.averageWeightKg ? `${item.averageWeightKg} kg` : "—"} />
                      <Info label="Last Fed" value={item.lastFed ? new Date(item.lastFed).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" }) : "—"} />
                      <Info label="Last Watered" value={item.lastWatered ? new Date(item.lastWatered).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" }) : "—"} />
                      <Info label="Fit for Transport" value={
                        <span className={item.fitForTransport ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>
                          {item.fitForTransport ? "✓ Declared" : "✗ Not declared"}
                        </span>
                      } />
                      <Info label="Vet Certificate" value={
                        <span className={item.vetCertificateUploaded ? "text-emerald-600 font-semibold" : "text-amber-500 font-semibold"}>
                          {item.vetCertificateUploaded ? "✓ Provided" : "Pending"}
                        </span>
                      } />
                    </div>
                    {item.notes && <p className="text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">{item.notes}</p>}
                  </div>
                ))}
              </SectionCard>
            )}

            {/* Documents */}
            {(booking.documents?.length > 0 || booking.onlineRequest) && (
              <SectionCard title="Documents & Attachments">
                {booking.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {booking.documents.map((doc, i) => {
                      const ext = doc.name?.split(".").pop()?.toLowerCase();
                      const icon = ["pdf"].includes(ext) ? "📋" : ["jpg","jpeg","png"].includes(ext) ? "🖼" : ["xlsx","xls","csv"].includes(ext) ? "📊" : "📄";
                      return (
                        <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span>{icon}</span>
                            <div>
                              <div className="text-sm font-medium text-slate-900">{doc.name}</div>
                              <div className="text-xs text-slate-400 capitalize">{doc.category?.replace(/_/g, " ") || ext || "Document"} · {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-NZ") : ""}</div>
                            </div>
                          </div>
                          {doc.url && (
                            <a href={doc.url} target="_blank" rel="noreferrer"
                              className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white">
                              View
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No documents uploaded.</p>
                )}
              </SectionCard>
            )}
          </div>

          <div className="space-y-6">
            <AsideCard title="Booking Summary">
              <SummaryRow label="Requested Sailing" value={booking.requestedSailingRef || "Unassigned"} />
              <SummaryRow label="Status" value={toTitle(booking.status || "pending")} />
              <SummaryRow label="Total Charges" value={money(booking.charges?.total)} />
              <SummaryRow label="Total Weight" value={`${totalWeight.toLocaleString()} kg`} />
            </AsideCard>

            <AsideCard title="Owner's Risk Statement">
              {booking.ownersRiskStatement?.executed ? (
                <>
                  <SummaryRow label="Status" value="Executed" />
                  <SummaryRow label="Signed By" value={booking.ownersRiskStatement.signedName || "-"} />
                  <SummaryRow
                    label="Executed At"
                    value={
                      booking.ownersRiskStatement.executedAt
                        ? new Date(booking.ownersRiskStatement.executedAt).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })
                        : "-"
                    }
                  />
                  <SummaryRow
                    label="Captured Via"
                    value={
                      booking.ownersRiskStatement.executedVia === "online_booking"
                        ? "Online Booking"
                        : booking.ownersRiskStatement.executedVia === "staff_manual"
                        ? "Staff (Manual)"
                        : "-"
                    }
                  />
                </>
              ) : (
                <p className="text-sm text-amber-600 font-medium">
                  Not yet executed — required before this booking can be confirmed.
                </p>
              )}
            </AsideCard>

            <AsideCard title="Payment & Invoice">
              <SummaryRow
                label="Status"
                value={
                  booking.invoice?.invoiceStatus === "Paid"
                    ? "Paid"
                    : booking.paymentLink?.url
                    ? "Link created"
                    : "No link yet"
                }
              />
              <SummaryRow
                label="Amount (incl. GST)"
                value={booking.paymentLink?.amount ? `$${Number(booking.paymentLink.amount).toFixed(2)}` : "-"}
              />
              <SummaryRow
                label="Created"
                value={booking.paymentLink?.createdAt
                  ? new Date(booking.paymentLink.createdAt).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })
                  : "Never"}
              />

              {["pending", "confirmed"].includes(currentStatus) && booking.invoice?.invoiceStatus !== "Paid" && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-4 focus-within:border-slate-950">
                    <span className="text-sm text-slate-400">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Custom amount ex GST (optional)"
                      className="w-full py-3 pl-1 text-sm outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={savingStatus}
                    onClick={handleCreatePaymentLink}
                    className="w-full rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {booking.paymentLink?.url ? "Refresh Payment Link" : "Get Payment Link"}
                  </button>
                </div>
              )}

              {booking.paymentLink?.url && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(booking.paymentLink.url);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2500);
                    } catch (e) { /* clipboard may be blocked */ }
                  }}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {linkCopied ? "Copied ✓" : "Copy Link"}
                </button>
              )}

              {["pending", "confirmed"].includes(currentStatus) && booking.invoice?.invoiceStatus !== "Paid" && (
                <div className="space-y-2 border-t border-slate-100 pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email invoice</div>
                  <input
                    type="email"
                    value={invoiceEmail}
                    onChange={(e) => setInvoiceEmail(e.target.value)}
                    placeholder={defaultInvoiceEmail || "Customer email"}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-950"
                  />
                  <button
                    type="button"
                    disabled={savingStatus}
                    onClick={handleSendStripeInvoice}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {booking.stripeInvoice?.id ? "Send Invoice Again" : "Send Invoice"}
                  </button>
                  <p className="text-xs text-slate-400">
                    Stripe emails an invoice due on receipt, payable by card or bank transfer. Uses the custom amount above if entered.
                  </p>
                </div>
              )}

              {booking.stripeInvoice?.id && (
                <div className="space-y-2">
                  <SummaryRow label="Invoice" value={booking.stripeInvoice.number || "Sent"} />
                  <SummaryRow label="Sent to" value={booking.stripeInvoice.sentTo} />
                  <SummaryRow
                    label="Sent at"
                    value={booking.stripeInvoice.sentAt
                      ? new Date(booking.stripeInvoice.sentAt).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })
                      : "-"}
                  />
                  {booking.stripeInvoice.url && (
                    <a
                      href={booking.stripeInvoice.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View Invoice
                    </a>
                  )}
                </div>
              )}
            </AsideCard>

            <AsideCard title="Moneyworks">
              <SummaryRow label="Invoice" value={booking.invoice?.invoiceNumber || "Not sent"} />
              <SummaryRow label="Status" value={booking.invoice?.invoiceStatus || "Not Invoiced"} />
              <SummaryRow label="Sequence #" value={booking.invoice?.moneyworksSequenceNumber || "-"} />
              <SummaryRow
                label="Sent At"
                value={hasMoneyworksInvoice && booking.invoice?.createdAt
                  ? new Date(booking.invoice.createdAt).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })
                  : "Never"}
              />
            </AsideCard>

            <AsideCard title="Customer Alert">
              {booking.customerAlert?.message ? (
                <div className={`rounded-xl border px-4 py-3 text-sm ${
                  booking.customerAlert.severity === "critical" ? "border-red-200 bg-red-50 text-red-800" :
                  booking.customerAlert.severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" :
                  "border-blue-200 bg-blue-50 text-blue-800"
                }`}>
                  <div className="font-semibold">{booking.customerAlert.message}</div>
                  {booking.customerAlert.setAt && (
                    <div className="mt-1.5 text-xs opacity-70">
                      Set {new Date(booking.customerAlert.setAt).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                      {booking.customerAlert.setBy ? ` by ${booking.customerAlert.setBy}` : ""}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No alert currently active.</p>
              )}
              <p className="mt-3 text-xs text-slate-400">
                Alerts are raised from the sailing this booking is on, and apply to every booking on that sailing at once — they can't be set individually here.
              </p>
            </AsideCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full space-y-4 p-6">
        {children}
      </div>
    </div>
  );
}

function SectionCard({ title, children, flush = false }) {
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-6 py-5">
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
      </div>
      <div className={flush ? "" : "p-6"}>{children}</div>
    </section>
  );
}

function AsideCard({ title, children }) {
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      <div className="mt-5 space-y-3">{children}</div>
    </aside>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 break-words text-xl font-bold text-slate-900">{value || "-"}</p>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
        {value || "-"}
      </span>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-900">{value ?? "-"}</div>
    </div>
  );
}

function WorkflowStep({ label, active, done, danger }) {
  const styles = danger
    ? active
      ? "bg-red-100 text-red-700 border-red-300"
      : "bg-white text-slate-400 border-slate-200"
    : active
    ? "bg-slate-950 text-white border-slate-950"
    : done
    ? "bg-green-100 text-green-700 border-green-300"
    : "bg-white text-slate-500 border-slate-200";

  return <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${styles}`}>{label}</div>;
}

function StatusBadge({ status }) {
  const clean = String(status || "pending").toLowerCase();

  const styles =
    clean === "confirmed"
      ? "bg-green-100 text-green-700"
      : clean === "invoiced"
      ? "bg-blue-100 text-blue-700"
      : clean === "completed"
      ? "bg-slate-900 text-white"
      : clean === "cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";

  return <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${styles}`}>{toTitle(clean)}</span>;
}

function SourceBadge({ source }) {
  const clean = String(source || "manual").toLowerCase();
  const styles = clean === "online" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700";

  return <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${styles}`}>{toTitle(clean)}</span>;
}

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}